import { Message } from '../../types/chat';
import { QueryValidationResult, validateQueryAsync } from '../../services/chat/queryValidator';
import { debug } from '../../../debugLogger';
import { AbstractWizard } from './AbstractWizard';
import { WizardFactory, IntentDetectionResult } from './wizardFactory';
import { LLMMessage, LLMResponse } from './type';

export class ConversationManager {
  private activeWizard: AbstractWizard | null = null;
  private userId: string;

  // Add this property to ConversationManager class
  private currentWizardConversation: LLMMessage[] = [];

  constructor() {
    this.userId = 'user-' + Date.now();
  }
  
  public isInSpecializedFlow(): boolean {
    return this.activeWizard !== null;
  }

  // Add this method to ConversationManager
  public async startSpecificWizard(
    wizardType: string, 
    message: string, 
    previousMessages: Message[] = []
  ): Promise<{
    response: string;
    isSpecializedFlow: boolean;
    isComplete: boolean;
    finalOutput?: string;
  }> {
    // Skip intent detection and directly create the specified wizard
    return this.startWizardFlow(wizardType, message, previousMessages);
  }

  public clearActiveWizard(): void {
    this.activeWizard = null;
    this.currentWizardConversation = [];
  }

  public async processMessage(
    message: string, 
    previousMessages: Message[] = [],
    validationResult?: QueryValidationResult
  ): Promise<{
    response: string;
    isSpecializedFlow: boolean;
    isComplete: boolean;
    finalOutput?: string;
  }>{
    
    debug.log(`[ConversationManager] Processing message: "${message.substring(0, 50)}..."`);
    
    // Continue active wizard conversation
    if (this.activeWizard) {
      return this.continueWizardFlow(message, previousMessages);
    }
    
    // First run the existing query validator
    const validation = validationResult || await validateQueryAsync(message);

    debug.log('[ConversationManager] Validation result: -------------- ', validation);

    // Handle weather requests using existing logic
    if (validation.type === 'weather') {
      return {
        response: `I'll check the weather for ${validation.location || 'your area'}`,
        isSpecializedFlow: false,
        isComplete: false
      };
    }
    
    // If itinerary intent detected by existing validator, use travel wizard
    if (validation.type === 'itinerary') {
      // Try using AbstractWizard for richer intent classification
      try {
        const intentResult = await WizardFactory.detectIntent(message);
        console.log('[ConversationManager] Intent detection result: ----- ', intentResult);
        if (intentResult.confidence > 0.6 && intentResult.wizardType === 'travel') {
          return this.startWizardFlow('travel', message, previousMessages);
        }
      } catch (error) {
        debug.error('[ConversationManager] Error in itinerary intent detection:', error);
      }
      
      // Fallback to using travel wizard based on validateQuery
      return this.startWizardFlow('travel', message, previousMessages);
    }

    // After handling itinerary intent
    if (validation.type === 'restaurant') {
      // Try using AbstractWizard for richer intent classification
      try {
        const intentResult = await WizardFactory.detectIntent(message);
        if (intentResult.confidence > 0.6 && intentResult.wizardType === 'restaurant') {
          return this.startWizardFlow('restaurant', message, previousMessages);
        }
      } catch (error) {
        debug.error('[ConversationManager] Error in restaurant intent detection:', error);
      }
      
      // Fallback to using restaurant wizard based on validateQuery
      return this.startWizardFlow('restaurant', message, previousMessages);
    }
    
    // Use WizardFactory to try detecting additional intents
    try {
      const intentResult = await WizardFactory.detectIntent(message);
      if (intentResult.confidence > 0.5 && intentResult.wizardType !== 'other') {
        return this.startWizardFlow(intentResult.wizardType, message, previousMessages);
      }
    } catch (error) {
      debug.error('[ConversationManager] Intent detection error:', error);
    }
    
    // Handle validateQuery's other intents or fall back to normal flow
    return {
      response: await this.getNormalResponse(message),
      isSpecializedFlow: false,
      isComplete: false
    };
  }

  // Add to ConversationManager:
  async processUserPrompt(
    wizardType: string,
    message: string,
    userId: string
  ): Promise<{
    response: string;
    isComplete: boolean;
    finalOutput?: string;
  }> {
    // Create wizard if not exists
    if (!this.activeWizard) {
      this.activeWizard = WizardFactory.createWizard(wizardType, userId);
      console.log(`[ConversationManager] Created new ${wizardType} wizard`);
    }
    
    // Process through wizard
    return this.continueWizardFlow(message, []);
  }

  // Add to ConversationManager class
  private readonly intentToWizardTypeMap: Record<string, string> = {
    'itinerary': 'travel',
    'weather': 'weather',
    'restaurant': 'restaurant',
    'location_info': 'location',
    // Add future mappings here
  };

  // Helper method to get the wizard type from an intent
  private getWizardTypeForIntent(intent: string): string {
    // Return the mapped type or use the intent as fallback if not found
    return this.intentToWizardTypeMap[intent] || intent;
  }

  // Updated startWizardFlow method
  private async startWizardFlow(intentType: string, message: string, previousMessages: Message[] = []): Promise<{
    response: string;
    isSpecializedFlow: boolean;
    isComplete: boolean;
    finalOutput?: string;
  }> {
    // Reset active wizard
    this.activeWizard = null;

    // Map intent type to wizard type
    const wizardType = this.getWizardTypeForIntent(intentType);
    
    // Initialize wizard conversation with only current message
    this.currentWizardConversation = [{ 
      role: 'user' as 'user', 
      content: message 
    }];
    
    try {
      this.activeWizard = WizardFactory.createWizard(wizardType, this.userId);
    } catch (error) {
      const intentData = await WizardFactory.detectIntent(message);
      this.activeWizard = WizardFactory.createDynamicWizard(
        this.userId,
        message,
        intentData.possibleFields
      );
    }
    
    if (!this.activeWizard) {
      throw new Error("Failed to create wizard");
    }
    
    // Process with ONLY the current message context
    const result = await this.activeWizard.process(this.currentWizardConversation);

    console.log("[ConversationManager] Start Wizard processing result:", result);
    
    if (result.done) {
      // If we have conversational text, use it instead of generating response
      //const response = result.conversationalText || await this.generateCompletionResponse(this.activeWizard);
      const response = result.conversationalText; 

      console.log("[ConversationManager] Final response (complete):", response.substring(0, 50));
      const formattedOutput = this.activeWizard.getFormattedOutput();
      const finalOutput = JSON.stringify(formattedOutput);
      //const response = await this.generateCompletionResponse(this.activeWizard);
      
      // Add assistant response to wizard conversation
      this.currentWizardConversation.push({ 
        role: 'assistant' as 'assistant', 
        content: response 
      });
      
      this.activeWizard = null;
      
      sessionStorage.setItem('wizardJustCompleted', 'true');

      return {
        response,
        isSpecializedFlow: true,
        isComplete: true,
        finalOutput
      };
    } else if (this.activeWizard.isAwaitingConfirmation()) {

      console.log("SHIT SIHT SHIT SHIT -- That Fucker is here")
      // Generate summary and ask for confirmation
      const summary = await this.generateCompletionResponse(this.activeWizard);
      const response = `${summary}\n\nDoes this look good to you? Let me know if you'd like to make any changes!`;
      
      this.currentWizardConversation.push({ 
        role: 'assistant', 
        content: response 
      });
      
      return {
        response,
        isSpecializedFlow: true,
        isComplete: false
      };
    } else {
      //const response = await this.generateResponse(this.activeWizard, this.currentWizardConversation);
      const response = result.conversationalText!;
      console.log("FUCK FUCK FUCK FUCk -- That bastard is jumping here")
      //await this.enhanceConversationalText(result.conversationalText)
      //await this.generateResponse(this.activeWizard, this.currentWizardConversation);
      //console.log("[ConversationManager] Final response (incomplete):", response.substring(0, 50));
      // Add assistant response to wizard conversation
      this.currentWizardConversation.push({ 
        role: 'assistant' as 'assistant', 
        content: response 
      });
      
      return {
        response,
        isSpecializedFlow: true,
        isComplete: false
      };
    }
  }

/*   private async continueWizardFlow_OG(message: string, previousMessages: Message[] = []): Promise<{
    response: string;
    isSpecializedFlow: boolean;
    isComplete: boolean;
    finalOutput?: string;
  }> {
    if (!this.activeWizard) {
      throw new Error("No active wizard");
    }

    // NEW: Check if user wants to break away
    const intentResult = await WizardFactory.detectIntent(message);
    
    // Break away if: different intent OR low confidence OR explicit "other"
    if (intentResult.wizardType === 'other' || 
        (intentResult.wizardType !== this.activeWizard.getConfig().id && intentResult.confidence > 0.5)) {
      
      // Clear wizard and return control to normal flow
      this.clearActiveWizard();
      
      // Return signal to handleSendMessage to use standard flow
      return {
        response: '',
        isSpecializedFlow: false,  // This tells handleSendMessage to use getStreamingChatResponse
        isComplete: false
      };
    }
  
    // Add current message to wizard conversation
    this.currentWizardConversation.push({ 
      role: 'user' as 'user', 
      content: message 
    });
    
    // Process through wizard
    const result = await this.activeWizard.process(this.currentWizardConversation);
    
    // Use the conversational part of the response directly
    //const response = result.conversationalText || "Can you tell me more about your travel preferences?";
    const response = result.conversationalText!;

    // Add assistant response to wizard conversation
    this.currentWizardConversation.push({ 
      role: 'assistant' as 'assistant', 
      content: response
    });
    
    if (result.done) {
      const formattedOutput = this.activeWizard.getFormattedOutput();
      const finalOutput = JSON.stringify(formattedOutput);
      this.activeWizard = null;
      sessionStorage.setItem('wizardJustCompleted', 'true');
      
      return {
        response,
        isSpecializedFlow: true,
        isComplete: true,
        finalOutput
      };
    } if (!result.done && this.activeWizard.isAwaitingConfirmation()) {
        const confirmationValue = result.extractedFields.confirmation;
        if (confirmationValue === false) {
          // User wants changes - reset confirmation and ask what to change
          this.activeWizard.setFieldValue('confirmation', null);
          const response = "What would you like to change? I can update the destination, dates, travel group, or activities.";
          
          return {
            response,
            isSpecializedFlow: true,
            isComplete: false
          };
        }
        return {
          response,
          isSpecializedFlow: true,
          isComplete: false
        };
      } else {
      return {
        response,
        isSpecializedFlow: true,
        isComplete: false
      };
    }
  } */

  private async continueWizardFlow(message: string, previousMessages: Message[] = []): Promise<{
    response: string;
    isSpecializedFlow: boolean;
    isComplete: boolean;
    finalOutput?: string;
  }> {
    if (!this.activeWizard) {
      throw new Error("No active wizard");
    }

    // NEW: Check if user wants to break away
    const intentResult = await WizardFactory.detectIntent(message);
    // Break away if: different intent OR low confidence OR explicit "other"
    /* if (intentResult.wizardType === 'other' || 
        (intentResult.wizardType !== this.activeWizard.getConfig().id && intentResult.confidence > 0.5)) {
      
      // Clear wizard and return control to normal flow
      this.clearActiveWizard();
      
      // Return signal to handleSendMessage to use standard flow
      return {
        response: '',
        isSpecializedFlow: false,  // This tells handleSendMessage to use getStreamingChatResponse
        isComplete: false
      };
    } */

    // Add current message to wizard conversation
    this.currentWizardConversation.push({ 
      role: 'user' as 'user', 
      content: message 
    });
    
    // Process through wizard
    const result = await this.activeWizard.process(this.currentWizardConversation);

    console.log("[ConversationManager] Continue Wizard processing result:", result);


    // Check if wizard's own prompt detected break-away
    if (result.extractedFields.breakAway) {
      this.clearActiveWizard();
      return {
        response: "No problem! Let me know if you need help with anything else.",
        isSpecializedFlow: false,
        isComplete: false
      };
    }
    
    // Use the conversational part of the response directly
    const response = result.conversationalText!;

    // Add assistant response to wizard conversation
    this.currentWizardConversation.push({ 
      role: 'assistant' as 'assistant', 
      content: response
    });
    
    if (result.done) {
      const formattedOutput = this.activeWizard.getFormattedOutput();
      const finalOutput = JSON.stringify(formattedOutput);
      this.activeWizard = null;
      sessionStorage.setItem('wizardJustCompleted', 'true');
      
      return {
        response,
        isSpecializedFlow: true,
        isComplete: true,
        finalOutput
      };
    } else if (!result.done && this.activeWizard.isAwaitingConfirmation()) {
      const confirmationValue = result.extractedFields.confirmation;
      if (confirmationValue === false) {
        // User wants changes - reset confirmation and ask what to change
        this.activeWizard.setFieldValue('confirmation', null);
        const response = "What would you like to change? I can update the destination, dates, travel group, or activities.";
        
        return {
          response,
          isSpecializedFlow: true,
          isComplete: false
        };
      }
      return {
        response,
        isSpecializedFlow: true,
        isComplete: false
      };
    } else {
      return {
        response,
        isSpecializedFlow: true,
        isComplete: false
      };
    }
  }

  // Updated continueWizardFlow method

  private async generateCompletionResponse(wizard: AbstractWizard): Promise<string> {
    let contentTemplate = wizard.getConfig().completionPrompt;
    
    // Replace placeholders (keep existing code)
    for (const field of wizard.getConfig().fieldDefinitions) {
      const value = wizard.getFieldValue(field.name);
      const placeholder = `{{${field.name}}}`;
      
      if (contentTemplate.includes(placeholder)) {
        contentTemplate = contentTemplate.replace(
          placeholder, 
          Array.isArray(value) ? JSON.stringify(value) : String(value || 'unknown')
        );
      }
    }
    
    try {
      // Set the session flag for context reset in future messages
      sessionStorage.setItem('wizardJustCompleted', 'true');
      
      /* const response = await fetch('/api/wizard', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          //system: "You're confirming travel/restaurant details in a natural, enthusiastic way. Sound like you're going to take action. Be warm but don't use phrases like 'I've gathered information' or 'I've collected details.' Use contractions and casual language. Keep it under 75 words.",
          system:"",
          messages: [{ role: 'user', content: contentTemplate }]
        })
      }); */

      const response = await fetch('/api/wizard', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          system: "You're confirming travel details. Create a friendly, conversational summary of the trip details provided. Be warm and enthusiastic. Keep it under 75 words.", // Add proper system prompt
          messages: [{ role: 'user', content: contentTemplate }]
        })
      });
      
      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }
      
      const data = await response.json();
      return data.content?.[0]?.text || "Perfect! I'll get everything set up based on your preferences.";
    } catch (error) {
      return "Perfect! I'll get everything set up based on your preferences.";
    }
  }

  private async generateResponse(wizard: AbstractWizard, messages: LLMMessage[]): Promise<string> {
    const fieldValues: Record<string, any> = {};
    
    for (const field of wizard.getConfig().fieldDefinitions) {
      fieldValues[field.name] = wizard.getFieldValue(field.name);
    }
    
    const missingFields = wizard.getConfig().fieldDefinitions
      .filter(field => field.required && !fieldValues[field.name])
      .map(field => field.name);
      
    try {
      // Get important context (location, dates if available)
      const location = fieldValues.destination || '';
      const dateInfo = fieldValues.startDate ? `on ${fieldValues.startDate}` : '';
      
      const prompt = `You're a helpful travel assistant having a conversation about ${location} ${dateInfo}.

      You know these details: ${Object.entries(fieldValues)
        .filter(([_, v]) => v && v !== 'unknown' && v !== 'Not provided')
        .map(([k, v]) => `${k.replace(/([A-Z])/g, ' $1').toLowerCase()}: ${v}`)
        .join(', ')}
      
      If the user has just mentioned about wanting to plan a vacation without providing any details whatsoever, start by asking where they'd like to go. Vary your response based on how they phrased their request. Be warm and enthusiastic.  

      Ask naturally about ${missingFields[0]} without mentioning it's "missing" or that you're "collecting information." 
      Be conversational and friendly. Don't say "Okay, let me ask about..." or similar phrases.
      
      Keep your response short under 60-70 words.`;
      
      const response = await fetch('/api/wizard', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          system: prompt,
          messages: [{ role: 'user', content: "Generate a response" }],
          temperature: 0.8
        })

      });
      
      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }
      
      const data = await response.json();
      return data.content[0].text || `What ${missingFields[0]} did you have in mind?`;
    } catch (error) {
      return `What ${missingFields[0]} did you have in mind?`;
    }
  }

  private async getNormalResponse(message: string): Promise<string> {
    console.log(" [ConversationManager] getNormalResponse:  ---------------------------- ", message);
    try {
      const response = await fetch('/api/wizard', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          system: "You are a helpful travel assistant.",
          messages: [{ role: 'user', content: message }]
        })
      });
      
      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }
      
      const data = await response.json();
      return data.content[0].text || 'Sorry, I encountered an error.';
    } catch (error) {
      return 'Sorry, I encountered an error.';
    }
  }
}